import { differenceInMonths, format, isAfter } from "date-fns";
import { useMemo } from "react";
import { useTheme } from "@mui/material";
import { keepPreviousData } from "@tanstack/react-query";
import {
  useDividendTimeline,
  usePublicDividendTimeline,
} from "@rentablo/api/apiv1";
import { DividendHistoryTree } from "@rentablo/api/apiv1/model";
import { usePublicOrPrivateData } from "@features/sharedPortfolio/hooks/usePublicOrPrivateData";
import { BarInvestmentDataProps } from "@typings/barInvestmentData";
import { getPortionPercent } from "@helpers/math";

export type DividendChartData = {
  historicDividends: BarInvestmentDataProps[];
  predictedDividends: BarInvestmentDataProps[];
  historicY: number;
  predictedY: number;
  y: number;
  name: string;
  year: number;
  month: number;
  color: string;
};

export const buildDividendChartData = (
  timelineData: DividendHistoryTree,
  startDate: Date,
  endDate: Date | undefined,
  interval: IntervalType,
  color: string
): DividendChartData[] => {
  if (!timelineData?.nodesByYear) return [];

  const referenceEnd = endDate ?? new Date();
  const monthsAmount = differenceInMonths(referenceEnd, startDate);
  const yearsAmount = monthsAmount / 12;
  const shouldFilterByPaymentDate = interval === "custom" && Boolean(endDate);
  const startTime = startDate.getTime();
  const endTime = referenceEnd.getTime();

  const isInSelectedRangeByPaymentDate = (node: {
    paymentDate?: string;
  }): boolean => {
    if (!shouldFilterByPaymentDate) return true;
    if (!node.paymentDate) return true; // fall back to bucket placement when absent
    const ts = Date.parse(node.paymentDate);
    if (Number.isNaN(ts)) return true;
    return ts >= startTime && ts <= endTime;
  };

  const getMonthBreakdown = (year: number, month: number) => {
    const monthNode = timelineData.nodesByYear?.[year]?.children?.[month];
    let historicY = 0;
    let predictedY = 0;
    const historicDividends: BarInvestmentDataProps[] = [];
    const predictedDividends: BarInvestmentDataProps[] = [];

    if (monthNode?.children) {
      Object.values(monthNode.children)
        .filter((inv) => inv.grossAmount > 0)
        .filter((inv) => isInSelectedRangeByPaymentDate(inv))
        .forEach((inv) => {
          const [investmentId] = inv.investmentIds;
          const invRefName =
            timelineData.investmentReferenceById?.[investmentId]
              ?.standardisedName;
          if (inv.isPrediction) {
            predictedY += inv.grossAmount;
            predictedDividends.push({
              amount: inv.grossAmount,
              invRefName,
            });
          } else {
            historicY += inv.grossAmount;
            historicDividends.push({
              amount: inv.grossAmount,
              invRefName,
            });
          }
        });
      const total = historicY + predictedY;
      historicDividends.forEach((d) => {
        d.percent = getPortionPercent(d.amount!, total);
      });
      predictedDividends.forEach((d) => {
        d.percent = getPortionPercent(d.amount!, total);
      });
    } else if (
      monthNode &&
      monthNode.grossAmount > 0 &&
      isInSelectedRangeByPaymentDate(monthNode)
    ) {
      if (monthNode.isPrediction) predictedY = monthNode.grossAmount;
      else historicY = monthNode.grossAmount;
    }

    return { historicY, predictedY, historicDividends, predictedDividends };
  };

  if (yearsAmount > 1) {
    const endYear = referenceEnd.getFullYear();
    const startYear = startDate.getFullYear();

    return Object.values(timelineData.nodesByYear)
      .filter(
        (yearNode) => yearNode.year >= startYear && yearNode.year <= endYear
      )
      .map((yearNode) => {
        let historicY = 0;
        let predictedY = 0;
        const historicByInv: Record<number, number> = {};
        const predictedByInv: Record<number, number> = {};

        if (yearNode.children) {
          Object.entries(yearNode.children).forEach(([monthKey, monthNode]) => {
            const month = Number(monthKey);

            if (monthNode.children) {
              const { historicY: monthHistoricY, predictedY: monthPredictedY } =
                getMonthBreakdown(yearNode.year, month);

              historicY += monthHistoricY;
              predictedY += monthPredictedY;

              Object.values(monthNode.children)
                .filter((inv) => inv.grossAmount > 0)
                .filter((inv) => isInSelectedRangeByPaymentDate(inv))
                .forEach((inv) => {
                  const [id] = inv.investmentIds;
                  const bucket = inv.isPrediction
                    ? predictedByInv
                    : historicByInv;
                  bucket[id] = (bucket[id] || 0) + inv.grossAmount;
                });
            } else {
              if (monthNode.grossAmount <= 0) return;
              if (!isInSelectedRangeByPaymentDate(monthNode)) return;

              if (monthNode.isPrediction) {
                predictedY += Math.max(0, monthNode.grossAmount);
              } else {
                historicY += Math.max(0, monthNode.grossAmount);
              }
            }
          });
        }

        const toList = (
          byInv: Record<number, number>,
          total: number
        ): BarInvestmentDataProps[] =>
          Object.entries(byInv).map(([id, amount]) => {
            const invRefName =
              timelineData.investmentReferenceById?.[Number(id)]
                ?.standardisedName;
            return {
              amount,
              invRefName,
              percent: getPortionPercent(amount, total),
            };
          });

        const grandTotal = historicY + predictedY;
        return {
          historicDividends: toList(historicByInv, grandTotal),
          predictedDividends: toList(predictedByInv, grandTotal),
          historicY,
          predictedY,
          y: historicY + predictedY,
          name: format(new Date(yearNode.year, 0), "yyyy"),
          year: yearNode.year,
          month: 0,
          color,
        };
      })
      .sort((a, b) => a.year - b.year);
  }

  // Monthly view (YTD, 1J, custom)
  const bars: DividendChartData[] = [];
  let d = new Date(startDate.getFullYear(), startDate.getMonth());
  const referenceEndMonth = new Date(
    referenceEnd.getFullYear(),
    referenceEnd.getMonth()
  );

  while (!isAfter(d, referenceEndMonth)) {
    const year = d.getFullYear();
    const month = d.getMonth();

    if (
      interval === "custom" &&
      endDate &&
      isAfter(d, new Date(endDate.getFullYear(), endDate.getMonth()))
    ) {
      break;
    }

    const { historicY, predictedY, historicDividends, predictedDividends } =
      getMonthBreakdown(year, month);

    bars.push({
      historicDividends,
      predictedDividends,
      historicY,
      predictedY,
      y: historicY + predictedY,
      name: format(d, "MMM").replace(".", ""),
      year,
      month,
      color,
    });

    d = new Date(year, month + 1);
  }

  // Detect duplicate month names and append year to duplicates
  const nameCount = new Map<string, number>();
  for (const bar of bars) {
    const count = (nameCount.get(bar.name) ?? 0) + 1;
    nameCount.set(bar.name, count);
    if (count > 1) {
      bar.name = `${bar.name} ${bar.year % 100}`;
    }
  }

  return bars;
};

export const useDividendsChartData = ({
  accountIds = [],
  startDate,
  endDate,
  interval,
  enabled = true,
  displayCurrency,
}: {
  accountIds?: number[];
  startDate: Date;
  endDate?: Date;
  interval: IntervalType;
  enabled?: boolean;
  displayCurrency?: string;
}): {
  chartData: DividendChartData[];
  currency?: string;
  isFetching: boolean;
  isFetched: boolean;
} => {
  const theme = useTheme();
  const referenceEnd = endDate ?? new Date();
  const endYear = referenceEnd.getFullYear();

  const {
    data: timelineData,
    isFetching,
    isFetched,
  } = usePublicOrPrivateData({
    privateMethod: useDividendTimeline,
    publicMethod: usePublicDividendTimeline,
    params: {
      accountId: accountIds,
      startYear: startDate.getFullYear(),
      endYear,
      includePredictions: true,
      displayCurrency,
    },
    options: {
      enabled: Boolean(accountIds.length) && enabled,
      refetchOnMount: false,
      placeholderData: keepPreviousData,
    },
  });

  const chartData = useMemo((): DividendChartData[] => {
    if (!timelineData) return [];
    const color = theme.palette.graphVisual["02"].mid;
    return buildDividendChartData(
      timelineData,
      startDate,
      endDate,
      interval,
      color
    );
  }, [timelineData, startDate, endDate, interval, theme]);

  return { chartData, isFetching, isFetched };
};
